<template>
  <div class="carga-ots-info">
    <!-- ===================== TOTALES DEL PERIODO ===================== -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold"># Órdenes en el periodo</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium">{{ fmtNum(totalOrdenes) }}</div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold">Cantidad total a producir</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium">{{ fmtNum(cantidadTotal) }}</div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mt-3">{{ error }}</v-alert>
    <v-alert v-else-if="mensaje" type="info" dense outlined class="mt-3">{{ mensaje }}</v-alert>

    <div class="barra mt-3">
      <v-text-field v-model="busqueda" dense outlined hide-details clearable label="Buscar"
        prepend-inner-icon="mdi-magnify" class="buscador" />
      <v-spacer />
      <span v-if="auditoria" class="caption grey--text">
        Auditoría: {{ prop(auditoria, 'carpeta') }}
        <template v-if="fechaAuditoria"> · {{ fechaAuditoria }}</template>
      </span>
    </div>

    <v-data-table :headers="headers" :items="ordenes" :search="busqueda" :loading="cargando"
      :items-per-page="25" :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
      class="mt-2" dense
      loading-text="Cargando... Espere, Por Favor." no-data-text="No hay órdenes de la última carga en este periodo.">
      <template v-slot:item.ClaveOT="{ item }">
        <span class="font-weight-medium">{{ item.ClaveOT }}</span>
        <v-chip v-if="item.Critica" x-small color="red" dark class="ml-1">Crítica</v-chip>
      </template>

      <template v-slot:item.Cantidad="{ item }">
        {{ fmtNum(item.Cantidad) }}
      </template>

      <template v-slot:item.FechaCompromisoEntrega="{ item }">
        {{ fmtFecha(item.FechaCompromisoEntrega) }}
      </template>

      <template v-slot:item.FechaInicioProduccion="{ item }">
        {{ fmtFecha(item.FechaInicioProduccion) }}
      </template>

      <template v-slot:item.RateProgramado="{ item }">
        {{ item.RateProgramado || '—' }}
      </template>

      <template v-slot:item.Estado="{ item }">
        <v-chip x-small :color="item.ExisteEnSistema ? 'green' : 'red'" dark
          :title="item.MotivoError || item.DetalleResultado || ''">
          {{ item.ExisteEnSistema ? 'Cargada' : 'No cargada' }}
        </v-chip>
      </template>

      <template v-slot:item.EstadoProduccion="{ item }">
        {{ item.ExisteEnSistema ? (item.EstadoProduccion || '—') : '—' }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from 'axios'

// Lee una propiedad aceptando camelCase o PascalCase (el back serializa en PascalCase).
const prop = (obj, camel) => {
  if (!obj) return undefined
  if (obj[camel] !== undefined) return obj[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return obj[pascal]
}

export default {
  name: 'CargaOtsInformacion',

  data() {
    return {
      cargando: false,
      error: '',
      mensaje: '',
      auditoria: null,
      ordenes: [],
      // Totales del periodo que calcula el back (solo renglones con fecha compromiso dentro
      // del rango): órdenes distintas y piezas a producir.
      totalOrdenes: 0,
      cantidadTotal: 0,
      busqueda: '',
    }
  },

  computed: {
    fechaAuditoria() {
      const f = prop(this.auditoria, 'fecha')
      return f ? this.fmtFecha(f) : ''
    },

    headers() {
      return [
        { text: 'ODT', value: 'ClaveOT' },
        { text: 'Pos.', value: 'Posicion', align: 'end' },
        { text: 'Artículo', value: 'Articulo' },
        { text: 'Cantidad', value: 'Cantidad', align: 'end' },
        { text: 'Máquina', value: 'Maquina' },
        { text: 'Molde', value: 'Molde' },
        { text: 'Almacén', value: 'Almacen' },
        { text: 'Fecha compromiso', value: 'FechaCompromisoEntrega' },
        { text: 'Inicio producción', value: 'FechaInicioProduccion' },
        { text: 'Rate', value: 'RateProgramado', align: 'end' },
        { text: 'Estado', value: 'Estado' },
        { text: 'Estado de producción', value: 'EstadoProduccion' },
      ]
    },
  },

  methods: {
    prop,

    fmtNum(n) {
      return Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })
    },

    fmtFecha(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return '—'
      const p = (n) => String(n).padStart(2, '0')
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },

    // Lo llama el contenedor con el rango vigente de los filtros.
    async cargar(inicio, fin) {
      this.cargando = true
      this.$emit('cargando', true)
      this.error = ''
      this.mensaje = ''
      try {
        const p = new URLSearchParams()
        if (inicio) p.append('inicio', inicio)
        if (fin) p.append('fin', fin)

        const { data } = await axios.get(`/CargaOts/Ordenes?${p.toString()}`)
        this.auditoria = prop(data, 'auditoria') || null
        this.ordenes = (prop(data, 'ordenes') || []).map((o) => this.normalizar(o))
        this.totalOrdenes = Number(prop(data, 'totalOrdenes')) || 0
        this.cantidadTotal = Number(prop(data, 'cantidadTotal')) || 0
        this.mensaje = prop(data, 'mensaje') || ''
      } catch (e) {
        this.ordenes = []
        this.auditoria = null
        this.totalOrdenes = 0
        this.cantidadTotal = 0
        this.error = (e && e.response && e.response.data && e.response.data.message)
          || 'No se pudieron consultar las órdenes de la última carga.'
        // eslint-disable-next-line no-console
        console.error('Error consultando las órdenes de la última carga', e)
      } finally {
        this.cargando = false
        this.$emit('cargando', false)
      }
    },

    // El back serializa en PascalCase, pero se normaliza para no depender del casing.
    normalizar(o) {
      return {
        ClaveOT: prop(o, 'claveOT'),
        Posicion: Number(prop(o, 'posicion')) || 0,
        Articulo: prop(o, 'articulo'),
        Cantidad: Number(prop(o, 'cantidad')) || 0,
        FechaCompromisoEntrega: prop(o, 'fechaCompromisoEntrega'),
        FechaInicioProduccion: prop(o, 'fechaInicioProduccion'),
        Cliente: prop(o, 'cliente'),
        Almacen: prop(o, 'almacen'),
        Maquina: prop(o, 'maquina'),
        Molde: prop(o, 'molde'),
        RateProgramado: prop(o, 'rateProgramado'),
        Estatus: prop(o, 'estatus'),
        Critica: !!prop(o, 'critica'),
        DetalleResultado: prop(o, 'detalleResultado'),
        MotivoError: prop(o, 'motivoError'),
        IdTrabajoOrden: prop(o, 'idTrabajoOrden'),
        ExisteEnSistema: !!prop(o, 'existeEnSistema'),
        EstadoProduccion: prop(o, 'estadoProduccion'),
        // Texto que alimenta el buscador de la tabla en la columna Estado.
        Estado: prop(o, 'existeEnSistema') ? 'Cargada' : 'No cargada',
      }
    },
  },
}
</script>

<style scoped>
.barra {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.buscador {
  max-width: 320px;
}
</style>
